"use client";
import { useId, Fragment, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Tab } from "@headlessui/react";

import { Ga4WithEventParameters } from "src/helpers/google-tag-manager/events/ga4-with-eventparameters";

import Title from "components/listing-detail/title";
import MoreContent from "components/listing-detail/more-content";

import styles from "./styles.module.css";

const CLICK_URL = "https://track.adform.net/C/?bn=83103756;gdpr=$%7Bgdpr%7D;gdpr_consent=$%7Bgdpr_consent_50%7D";
const IMPRESSION_URL =
  "https://track.adform.net/adfserve/?bn=83103756;1x1inv=1;srctype=3;gdpr=${gdpr};gdpr_consent=${gdpr_consent_50};ord=[timestamp]";
const CREATIVE_BN = "83103756";

const PropertiesListWrap = ({ data, hasFiber }) => {
  const id = useId();

  const bannerRef = useRef(null);

  const handleSetGa4Event = () => {
    Ga4WithEventParameters({
      eventName: "ilan_banner",
      eventParameters: { widget: "vodafone_ic_ozellikler" },
    });
  };

  useEffect(() => {
    if (!hasFiber || typeof window === "undefined" || typeof IntersectionObserver === "undefined") return;

    if (!window.__ADFORM_IMP_SENT__) window.__ADFORM_IMP_SENT__ = new Set();
    const sentSet = window.__ADFORM_IMP_SENT__;
    const LOCK_KEY = `adform_imp_${CREATIVE_BN}_${window.location?.pathname || "page"}`;
    if (sentSet.has(LOCK_KEY)) return;

    const el = bannerRef.current;
    if (!el) return;

    let timer = null;
    let localSent = false;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting && !localSent && !sentSet.has(LOCK_KEY)) {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => {
            if (localSent || sentSet.has(LOCK_KEY)) return;
            localSent = true;
            sentSet.add(LOCK_KEY);

            const url = IMPRESSION_URL.replace("[timestamp]", Date.now().toString());

            fetch(url, { method: "GET", mode: "no-cors" }).catch(() => {});
            io.disconnect();
          }, 500);
        } else if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: 0.5 }
    );

    io.observe(el);

    return () => {
      if (timer) clearTimeout(timer);
      io.disconnect();
    };
  }, [hasFiber]);

  return (
    <div className={styles.wrapper}>
      <Title>İlan Özellikleri</Title>

      {hasFiber && (
        <div className={styles.vodafoneBannerWrapper} ref={bannerRef} onClick={handleSetGa4Event}>
          <a
            href={CLICK_URL}
            title="vodafone_banner_image"
            target="_blank"
            rel="noopener noreferrer nofollow sponsored"
          >
            <img
              src="/assets/images/vodafone-banner.jpeg"
              alt="vodafone banner"
              className={styles.imageClass}
              loading="lazy"
            />
          </a>
        </div>
      )}

      <div className={styles.inner}>
        <Tab.Group>
          <Tab.List className={styles.tabTitles}>
            {Object.entries(data).map(([key], index) => (
              <Tab key={`${id}-${index}`}>{key}</Tab>
            ))}
          </Tab.List>
          <Tab.Panels className={styles.tabContents}>
            {Object.entries(data).map(([key, value], index) => (
              <Tab.Panel key={`${id}-${index}`}>
                <MoreContent defaultHeight={245}>
                  {Object.entries(value).map(([title, contentList], idx) => (
                    <Fragment key={idx}>
                      <div className={styles.tabContentTitle}>{title}</div>
                      <ul className={styles.tabContentList}>
                        {contentList.map((item, listIndex) => (
                          <li key={`${id}+${listIndex}`}>{item}</li>
                        ))}
                      </ul>
                    </Fragment>
                  ))}
                </MoreContent>
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
};

PropertiesListWrap.propTypes = {
  data: PropTypes.object,
  hasFiber: PropTypes.bool,
};

export default PropertiesListWrap;
